<script lang="ts">
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import type { ContactFormInput } from "../../../../core/schema/ContactForm.js";
	import sendContactForm from "../../api/mutations/sendContactForm.graphql";
	import LabelledInput from "../Form/LabelledInput.svelte";
	import GdprCheckbox from "../Form/GdprCheckbox.svelte";
	import Button from "../Button.svelte";
	import Modal from "../Modal.svelte";
	import Form from "../Form/Form.svelte";
	import Message from "../Message.svelte";
	import { mutation } from "../../api/graphql.js";
	import translations from "./CareerForm.translations.json";
	import arrowRightLong from "../../../../core/assets/icons/arrow-right-long.svg?raw";
	import FileUploader from "../Form/FileUploader.svelte";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import type { Attachment } from "../../../../core/schema/Attachment.js";
	import type { Position } from "../../../../core/schema/Position.js";
	import { ContactFormType } from "../../../../core/schema/ContactFormType.js";
	import { createEventDispatcher } from "svelte";
	import { getSharedGlobalSettings } from "../../getSharedGlobalSettings.js";
	import { getSharedTranslate } from "../../utils/getSharedTranslate.js";
	import type { VertiType } from "../../../../core/schema/VertiType.js";

	export let vertiType: VertiType;
	export let position: Position;

	const translate = getSharedTranslate(translations);
	const dispatch = createEventDispatcher<{
		// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
		close: void;
	}>();

	const { TENANT_ID, PUBLIC_GRAPHQL_ENDPOINT } = getSharedGlobalSettings();

	const send = createPromiseStore(async (input: ContactFormInput) => {
		await mutation<boolean>(PUBLIC_GRAPHQL_ENDPOINT, sendContactForm, { input });
	});

	let uploadedFile: UploadedFile | undefined;
	let showSuccessModal = false;
	let finished = false;
	async function onsubmit(event: SubmitEvent): Promise<void> {
		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);

		const attachment: Attachment | undefined = uploadedFile && {
			...uploadedFile,
			title: uploadedFile.title ?? "CV",
		};

		const input: ContactFormInput = {
			vertiType,
			email: (formData.get("email") as string).trim(),
			file: attachment,
			gdprApprovedAt: formData.get("gdprApprovedAt") as string,
			message: (formData.get("message") as string).trim(),
			name: (formData.get("name") as string).trim(),
			pageUrl: window.location.href,
			phoneNumber: (formData.get("phoneNumber") as string).trim(),
			position: position.id,
			tenantId: TENANT_ID,
			type: ContactFormType.Career,
		};
		await send.call(input);
		showSuccessModal = true;
		finished = true;
	}

	let form: HTMLFormElement | null = null;
	$: {
		if (finished && !showSuccessModal) {
			form?.reset();
			uploadedFile = undefined;
			dispatch("close");
		}
	}

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<Modal bind:show={showSuccessModal}>
	<p class="h2" slot="title">{translate("thanks")}</p>
	<div class="flex flex-col items-center gap-2">
		<p>{translate("willBeInTouch")}</p>

		<Button
			on:click={() => {
				showSuccessModal = false;
			}}
		>
			{translate("understand")}
		</Button>
	</div>
</Modal>

<Form bind:form id="contact-form" {onsubmit}>
	<h2>{position.name}</h2>

	<div class="grid w-full items-end gap-y-3">
		<LabelledInput id="name" required autocomplete="given-name">{translate("name")}</LabelledInput>
		<LabelledInput id="phoneNumber" required type="tel" autocomplete="tel" inputProps={{ pattern: "[+]?[\\d ]+" }}>
			{translate("phone")}
		</LabelledInput>
		<LabelledInput id="email" required type="email" autocomplete="email">{translate("email")}</LabelledInput>
		<div class="relative w-full">
			<textarea
				id="message"
				name="message"
				class="input-with-label peer h-24 w-full rounded-lg border border-gray-200 px-3 !pb-4 !pt-[1.25rem] text-xs focus:font-semibold focus:outline-none lg:px-4"
				placeholder=" "
				required
				minlength="10"
			></textarea>
			<label class="label textarea-label" for="message">{translate("yourMessage")}<span>&nbsp;*</span></label>
		</div>
		<FileUploader
			destinationFolder="cv"
			currentFiles={uploadedFile ? [uploadedFile] : []}
			on:change={({ detail: { files } }) => {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				uploadedFile = files;
			}}
			required
			white
			round
			border={false}
		>
			{translate("uploadCurriculum")}
		</FileUploader>
	</div>
	<p class="text-2xs mt-2 text-center lg:text-left">*{translate("compulsoryInfo")}</p>

	<GdprCheckbox class="mb-4 justify-center lg:justify-start" />

	<Button
		class="mx-auto lg:mx-[initial]"
		icon={arrowRightLong}
		type="submit"
		disabled={$send.status === "loading"}
		variant="primary"
	>
		{#if $send.status === "loading"}
			{translate("submitting")}…
		{:else}
			{translate("submit")}
		{/if}
	</Button>

	{#if $send.status === "error"}
		<Message variant="error" class="mt-4">{translate("somethingWrong")}</Message>
	{/if}
</Form>
